import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  gamesQuery,
  getGamesSessions,
  scenarioLevelsQuery,
  scoresInsightsQuery
} from './api';

import { popupMessagesSummaryBreakdown } from './data';

import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';
import Analysis from '../UI/game/analysis';

export default function NormalBalanceSummaryBreakdown() {
  const navigate = useNavigate();
  
  const { 
    getUser,
    getCurrentNormalGameId
  } = useAuth();

  const user = getUser();
  
  const gameId = getCurrentNormalGameId();

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  const [scenarioId, setScenarioId] = useState(0);

  const [data, setData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [popupMessages, setPopupMessages] = useState(popupMessagesSummaryBreakdown);
  const textAdded = useRef(false);

  const { data: gamesSessions } = useQuery({
    queryKey: ['gamesSessionsSummaryBreakdown'],
    queryFn: getGamesSessions,
    refetchOnWindowFocus: false,
  });

  const { data: games } = useQuery(gamesQuery()) || {};
  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({scenarioId}));
  const { data: scoresInsights } = useQuery(scoresInsightsQuery({gameId, gameSessionId: currentGameSessionId}));

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionId(completedGameSession?.id);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('Normal Balance') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);

  const calculateAverage = (data, prop) => {
    if (!data || data.length === 0) return 0;

    let sum = 0;
    let count = 0;

    data.forEach(record => {
      const value = record.accuracy[prop];
      if (value !== null && value !== undefined) {
        sum += value;
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  };
  
  useEffect(() => {
    if(scoresInsights && scenarioLevels?.length) {
      setDataLoaded(true);
      const theElementsScore = calculateAverage(scoresInsights, 'elements');

      const firstSectionData = [
        ...(theElementsScore ? [{ title: "The Elements", score: theElementsScore * 100 || 0, color: '#B4E6EA' }] : [])
      ];

      const assetsScore = calculateAverage(scoresInsights, 'assets');
      const liabilitiesScore = calculateAverage(scoresInsights, 'liabilities');
      const equityScore = calculateAverage(scoresInsights, 'equity');
      const revenuesScore = calculateAverage(scoresInsights, 'revenues');
      const expensesScore = calculateAverage(scoresInsights, 'expenses');
      const secondSectionData = [
        ...(assetsScore ? [{ title: "Assets", score: assetsScore * 100, color: '#B4E6EA' }] : []),
        ...(liabilitiesScore ? [{ title: "Liabilities", score: liabilitiesScore * 100, color: '#B4E6EA' }] : []),
        ...(equityScore ? [{ title: "Equity", score: equityScore * 100, color: '#B4E6EA' }] : []),
        ...(revenuesScore ? [{ title: "Revenues", score: revenuesScore * 100, color: '#B4E6EA' }] : []),
        ...(expensesScore ? [{ title: "Expenses", score: expensesScore * 100, color: '#B4E6EA' }] : [])
      ];
      
      const accrualsDeferralsScore = calculateAverage(scoresInsights, 'accruals_deferrals');
      const contraAccountsScore = calculateAverage(scoresInsights, 'contra_accounts');
      const thirdSectionData = [
        ...(accrualsDeferralsScore ? [{ title: "Accruals & Deferrals", score: accrualsDeferralsScore * 100, color: '#B4E6EA' }] : []),
        ...(contraAccountsScore ? [{ title: "Contra-Accounts", score: contraAccountsScore * 100, color: '#B4E6EA' }] : [])
      ];
      
      const data = [
        ...(firstSectionData.length > 0 ? [{
          bars: firstSectionData,
          barsLabel: '%'
        }] : []),
        ...(secondSectionData.length > 0 ? [{
          title: "Various Accounts Under The Elements",
          bars: secondSectionData,
          barsLabel: '%'
        }] : []),
        ...(thirdSectionData.length > 0 ? [{
          title: "Tricky Accounts",
          bars: thirdSectionData,
          barsLabel: '%'
        }] : []),
      ];
      setData(data);
    }
  }, [scoresInsights, scenarioLevels]);

  const handlePopupMessagesNextButtonClick = () => {
    navigate('/normal/summary/decision-breakdown');
  }

  const handlePopupMessagesPrevButtonClick = () => {
    navigate('/normal/summary');
  }

  const maxScore = Math.max(...data.flatMap(section => 
    section.bars?.map(bar => bar.score) || []
  ));

  useEffect(() => {
    if (data.length > 0 && !textAdded.current) {
      const updatedMessages = [...popupMessages];
      let additionalText = '';
      
      if (data.some(item => !item.title)) {
        additionalText += '<h3>The Elements</h3><p>The Elements (Assets, Liabilities, Equity, Revenues, and Expenses) are the core building blocks of financial statements. You should know with 100% certainty which belong on the balance sheet and which on the income statement.</p><br>';
      }
      
      if (data.some(item => item.title === "Various Accounts Under The Elements")) {
        additionalText += '<h3>Various Accounts within The Elements</h3><p>You also decided the normal balance for numerous accounts. These are accounts you\'ll encounter throughout the semester, so it\'s important to know their correct classification from the start.</p><br>';
      }
      
      if (data.some(item => item.title === "Tricky Accounts")) {
        additionalText += '<h3>Tricky Accounts</h3><p>Finally, some accounts can be tricky because of how they\'re worded (for example, prepaid expenses are not actually classified as Expenses)</p>';
      }
      
      if (additionalText) {
        updatedMessages[0] = {
          ...updatedMessages[0],
          text: updatedMessages[0].text + additionalText
        };
        
        setPopupMessages(updatedMessages);
      }
      
      textAdded.current = true;
    }
  }, [data]);
  
  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        dataLoaded &&
          <div className="game-summary-container">
            <PopupMessages
              messages={popupMessages}
              messageSize="small"
              buttonText="Next"
              onButtonClick={handlePopupMessagesNextButtonClick}
              additionalButtonText="Back"
              onAdditionalButtonClick={handlePopupMessagesPrevButtonClick}
            />
            <Analysis
              heading="Accuracy Breakdown"
              data={data}
              size="small"
              maxScore={maxScore}
            />
          </div>
      }
    </Layout>
  );
}
